import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Play, Wifi, WifiOff } from 'lucide-react'
import api from '../lib/api'
import { useAgent } from 'agents/react'
import { AGENT_NAMES, DEFAULT_INSTANCE } from '../lib/agent-api'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

interface AgentState {
  id: string
  name: string
  status: string
  lastActivity: string
  metadata: Record<string, unknown>
}

export default function Agents() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [newAgent, setNewAgent] = useState({ name: '', type: 'coder', prompt: '' })

  // ─── REST data queries for agent list / CRUD ─────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get('/agent/list').then(res => res.data)
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/agent/create', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      setShowCreate(false)
      setNewAgent({ name: '', type: 'coder', prompt: '' })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/agent/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
    }
  })

  // ─── Real-time connection status for known agents ────────────────────
  const agentHost = isDev ? undefined : AGENT_WORKER_HOST

  const chatAgent = useAgent<AgentState>({
    agent: 'chat-agent',
    name: DEFAULT_INSTANCE,
    host: agentHost,
  })

  const orchAgent = useAgent<AgentState>({
    agent: 'orchestrator-agent',
    name: DEFAULT_INSTANCE,
    host: agentHost,
  })

  // Build a lookup map of agent name → whether connected
  const connectionMap: Record<string, boolean> = {
    [AGENT_NAMES.ChatAgent]: chatAgent.identified,
    [AGENT_NAMES.OrchestratorAgent]: orchAgent.identified,
  }

  if (isLoading) return <div>Loading...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Agents</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Agent
        </button>
      </div>

      {showCreate && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
          <h3 className="text-lg font-semibold mb-4">Create New Agent</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={newAgent.name}
                onChange={e => setNewAgent({ ...newAgent, name: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500"
                placeholder="Agent name"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Type</label>
              <select
                value={newAgent.type}
                onChange={e => setNewAgent({ ...newAgent, type: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500"
              >
                <option value="coder">Coder</option>
                <option value="deployer">Deployer</option>
                <option value="reviewer">Reviewer</option>
                <option value="orchestrator">Orchestrator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Prompt</label>
              <textarea
                value={newAgent.prompt}
                onChange={e => setNewAgent({ ...newAgent, prompt: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-500 h-24"
                placeholder="System prompt for the agent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => createMutation.mutate(newAgent)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {data?.data?.map((agent: any) => {
          const isConnected = connectionMap[agent.name]
          return (
            <div key={agent.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{agent.name}</h3>
                    {isConnected !== undefined && (
                      isConnected
                        ? <Wifi className="w-4 h-4 text-green-400" />
                        : <WifiOff className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">{agent.type}</p>
                  {agent.prompt && (
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{agent.prompt}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs ${
                    agent.status === 'active' ? 'bg-green-900 text-green-300' :
                    agent.status === 'busy' ? 'bg-blue-900 text-blue-300' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {agent.status}
                  </span>
                  <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                    <Play className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(agent.id)}
                    className="p-2 hover:bg-red-900 rounded-lg transition-colors text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
